import type {
    ChannelRow,
    DateRange,
    SalesInsightsContext,
    SkuRow,
} from './types';

/**
 * Assembles structured context for the AI insights endpoint from
 * already-computed sales state. No additional API calls are made.
 */
export function buildInsightsContext({
    dateRange,
    skuRows,
    channelRows,
    totalUnits,
    totalRevenue,
    dailyRunRate,
    dailyRevenueRunRate,
    revenueGrowth,
    unitsGrowth,
}: {
    dateRange: DateRange;
    skuRows: SkuRow[];
    channelRows: ChannelRow[];
    totalUnits: number;
    totalRevenue: number;
    dailyRunRate: number;
    dailyRevenueRunRate: number;
    revenueGrowth: string;
    unitsGrowth: string;
}): SalesInsightsContext {
    const totalOrders = channelRows.reduce((sum, c) => sum + c.orders, 0);
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    // Parse growth strings like "+12.3%" or "-5.1%" into numbers
    const parseGrowth = (g: string): number | null => {
        const num = parseFloat(g.replace(/[+%]/g, ''));
        return Number.isFinite(num) ? num : null;
    };

    return {
        dateRange: {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
        },
        topSkus: skuRows.slice(0, 10).map((s) => ({
            name: s.name,
            sku: s.sku,
            units: s.units,
            revenue: s.revenue,
            growth: s.growth,
        })),
        channels: channelRows.map((c) => ({
            name: c.channelId,
            units: c.units,
            revenue: c.revenue,
            share: c.share,
            growth: c.growth,
        })),
        kpis: {
            totalUnits,
            totalRevenue,
            totalOrders,
            avgOrderValue,
            dailyRunRate,
            dailyRevenueRunRate,
        },
        periodComparison: {
            revenueTrend: parseGrowth(revenueGrowth),
            unitsTrend: parseGrowth(unitsGrowth),
        },
    };
}
